/**
 * Menu lateral em cascata — a navegação padrão dos módulos de computador.
 *
 * Pedido do Lucas (04/08): o mesmo menu lateral cascateável em todos os
 * módulos, *"para criar uma estabilidade visual dentro do módulo"*. Antes cada
 * módulo navegava de um jeito — abas no topo no Escritório, barra horizontal
 * de categorias nos Pedidos, chips na Administração — e cada tela reensinava
 * a pessoa a se mover.
 *
 * O componente é um só e as telas o alimentam com itens; a cascata abre e
 * fecha como a árvore da vitrine (que foi o molde): clicar num nível com
 * filhos abre; clicar de novo fecha; o nível ativo fica marcado.
 *
 * NO CELULAR ele vira um botão que abre e fecha, e escolher um item fecha
 * sozinho — menu aberto em tela de 375px é uma parede na frente do conteúdo.
 */

import { escapar } from './formato.js';

export interface ItemMenu {
  chave: string;
  nome: string;
  emoji?: string;
  /** Número à direita (contagem de itens, erros abertos…). */
  badge?: number;
  filhos?: ItemMenu[];
  /** Linha fina acima — separa grupos de assuntos sem criar nível. */
  separaAntes?: boolean;
}

export interface MenuLateral {
  /** Marca o item ativo (e abre os pais dele). */
  marcar(chave: string | null): void;
  /** Troca os itens (ex.: quando a árvore de categorias chega da API). */
  reabastecer(itens: ItemMenu[]): void;
}

export function montarMenuLateral(
  raiz: HTMLElement,
  itensIniciais: ItemMenu[],
  aoEscolher: (chave: string) => void,
): MenuLateral {
  let itens = itensIniciais;
  let ativa: string | null = null;
  /** Quais nós com filhos estão abertos. */
  const abertos = new Set<string>();

  raiz.classList.add('ml');
  raiz.innerHTML = `
    <button class="ml-abrir" aria-expanded="false">☰ Menu</button>
    <nav class="ml-nav"></nav>`;
  const botao = raiz.querySelector<HTMLElement>('.ml-abrir')!;
  const nav = raiz.querySelector<HTMLElement>('.ml-nav')!;

  botao.addEventListener('click', () => {
    const aberto = raiz.classList.toggle('aberta');
    botao.setAttribute('aria-expanded', String(aberto));
  });

  function no(item: ItemMenu, nivel: number): string {
    const temFilhos = !!item.filhos?.length;
    const aberto = abertos.has(item.chave);
    return `
      ${item.separaAntes ? '<hr class="ml-separa">' : ''}
      <button class="ml-no n${nivel}" data-chave="${escapar(item.chave)}"
              aria-pressed="${ativa === item.chave}">
        <span class="ml-seta">${temFilhos ? (aberto ? '▾' : '▸') : ''}</span>
        ${item.emoji ? `<span class="ml-emoji">${item.emoji}</span>` : ''}
        <span class="ml-nome">${escapar(item.nome)}</span>
        ${item.badge != null && item.badge > 0 ? `<span class="ml-badge">${item.badge}</span>` : ''}
      </button>
      ${
        temFilhos && aberto
          ? `<div class="ml-filhos">${item.filhos!.map((f) => no(f, nivel + 1)).join('')}</div>`
          : ''
      }`;
  }

  function desenhar() {
    nav.innerHTML = itens.map((i) => no(i, 1)).join('');
  }

  nav.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('.ml-no');
    if (!b) return;
    const chave = b.dataset['chave']!;
    const item = achar(itens, chave);

    if (item?.filhos?.length) {
      // Nó com filhos: alterna a cascata E seleciona — como na vitrine, ver a
      // categoria inteira é um destino válido, não só um caminho.
      if (abertos.has(chave)) abertos.delete(chave);
      else abertos.add(chave);
    }
    ativa = chave;
    desenhar();
    // No celular o menu fecha ao escolher: ficou escolhido, não precisa da parede.
    raiz.classList.remove('aberta');
    aoEscolher(chave);
  });

  function achar(lista: ItemMenu[], chave: string): ItemMenu | null {
    for (const i of lista) {
      if (i.chave === chave) return i;
      const f = i.filhos ? achar(i.filhos, chave) : null;
      if (f) return f;
    }
    return null;
  }

  /** Abre a cascata até um item — para `marcar()` funcionar em nó fundo. */
  function abrirAte(lista: ItemMenu[], chave: string, trilha: string[]): boolean {
    for (const i of lista) {
      if (i.chave === chave) {
        for (const t of trilha) abertos.add(t);
        return true;
      }
      if (i.filhos && abrirAte(i.filhos, chave, [...trilha, i.chave])) return true;
    }
    return false;
  }

  desenhar();
  return {
    marcar(chave) {
      ativa = chave;
      if (chave) abrirAte(itens, chave, []);
      desenhar();
    },
    reabastecer(novos) {
      itens = novos;
      desenhar();
    },
  };
}
